import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AreaList from "@features/booking/components/AreaList";
import { BookingBreadcrumbs } from "@features/booking/components/BookingBreadcrumbs";
import { BookingPageLayout } from "@features/booking/layouts";
import { useBooking } from "@features/booking/state";
import { useBookingFlow, isPaymentGuarantee, shouldShowExperience } from "@features/booking/flow";
import { filterSlotMenusByArea } from "@features/booking/utils/areaUtils";
import { useRestaurant } from "@core/tenant/TenantProvider";

const AreaSelector = () => {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const {
    selectedTime,
    timeSlots,
    selectedArea,
    setSelectedArea,
    setSelectedExperience,
    areaFilterIds,
  } = booking;
  const { goToNextWith } = useBookingFlow();

  const selectedSlot = useMemo(() => {
    if (!selectedTime || !timeSlots) return null;
    return timeSlots.find((s) => s.hour === selectedTime) ?? null;
  }, [selectedTime, timeSlots]);

  const slotAreas = useMemo(() => {
    const all = selectedSlot?.availableAreas ?? [];
    if (!areaFilterIds || areaFilterIds.length === 0) return all;
    const filterSet = new Set(areaFilterIds);
    return all.filter((a) => filterSet.has(a.id));
  }, [selectedSlot, areaFilterIds]);

  // Defensive guard: if the user lands here without slot context or the slot
  // has no bookable areas left (e.g., stale session, direct link, back/forward),
  // bounce to /select-time to avoid a dead-end screen.
  const shouldBounce = !selectedTime || !slotAreas.some((a) => a.available);
  useEffect(() => {
    if (shouldBounce) navigate('/select-time', { replace: true });
  }, [shouldBounce, navigate]);

  if (shouldBounce) return null;

  const handleSelectArea = (areaId: number) => {
    const area = slotAreas.find((a) => a.id === areaId);
    if (!area || !area.available) return;

    // Compute routing locally — the orchestrator's activeSteps is memoized
    // against stale context while React batches the state update below.
    const enabledMenusForArea = filterSlotMenusByArea(
      (selectedSlot?.menus ?? []).filter((m) => !m.disabled),
      area,
    );
    // Use the shared EXPERIENCE predicate (single source of truth with
    // steps.ts EXPERIENCE.isRequired and handleSelectTime) rather than inlining
    // the rule — keeps the area-selection branch from drifting out of sync.
    const singleMenu = enabledMenusForArea.length === 1 ? enabledMenusForArea[0] : null;
    const needsExperience = shouldShowExperience({
      isWaitingList: booking.flowType === 'waiting_list',
      preSelectedMenuId: booking.preSelectedMenuId ?? null,
      forceShowMenuSelection: !!restaurant.forceShowMenuSelection,
      publicSearchMode: restaurant.publicSearchMode,
      menuCount: enabledMenusForArea.length,
      singleMenuHasGuarantee: !!(singleMenu &&
        isPaymentGuarantee(singleMenu.guarantee?.type, singleMenu.guarantee?.fee)),
      singleMenuHasPrice: !!(singleMenu && singleMenu.price && singleMenu.price.trim() !== ''),
      singleMenuIsEvent: !!(singleMenu && singleMenu.eventMenu),
      isSpecialDay: !!booking.isSelectedDateSpecial,
    });

    // Commit selectedArea (+ selectedExperience when auto-assigning) with normal
    // setters, and capture the same values so goToNextWith() resolves the next
    // step from them directly — no flushSync, no stale-snapshot hazard.
    const nextArea = { id: String(area.id), name: area.name };
    setSelectedArea(nextArea);

    let nextExperience = booking.selectedExperience;
    if (enabledMenusForArea.length === 1 && !needsExperience) {
      nextExperience = { id: String(singleMenu!.id), title: singleMenu!.name };
      setSelectedExperience(nextExperience);
    }

    if (needsExperience) {
      navigate('/select-experience');
      return;
    }
    goToNextWith({ selectedArea: nextArea, selectedExperience: nextExperience });
  };

  return (
    <BookingPageLayout
      title={t('AREA_TITLE')}
      subtitle={t('AREA_SUBTITLE')}
      subtitleClassName="text-foreground/50 text-sm mb-6 text-center"
      contentClassName=""
      breadcrumbs={<BookingBreadcrumbs show={['DINERS', 'SERVICE']} />}
    >
      <AreaList
        areas={slotAreas}
        selectedArea={selectedArea?.id ?? null}
        onSelect={handleSelectArea}
        publicAreas={restaurant.publicAreas}
      />
    </BookingPageLayout>
  );
};

export default AreaSelector;
